import { useEffect, useMemo, useState } from 'react';

import { useFormDataQuery } from '@/api/getters';
import Card, { CardDeadEndHint } from '@/components/Card';
import { ConditionalRender } from '@/components/ui/ConditionalRender';
import { Hint } from '@/components/ui/Hint';
import { FLOODING } from '@/consts';
import {
  CheckboxGroupWhatWasRepaired,
  WHAT_WAS_REPAIRED_NAME,
  WHAT_WAS_REPAIRED_OPTIONS,
} from '@/features/v2/hh/components/CheckboxGroupWhatWasRepaired';
import {
  HOW_MUCH_REPAIR_COST_NAME,
  InputHowMuchRepairCost,
} from '@/features/v2/hh/components/InputHowMuchRepairCost';
import {
  HOW_MUCH_INSURER_PAID_NAME,
  InputHowMuchTheInsurerPaid,
} from '@/features/v2/hh/components/InputHowMuchTheInsurerPaid';
import {
  ANY_INVOICES_NAME,
  RadioGroupAnyInvoices,
} from '@/features/v2/hh/components/RadioGroupAnyInvoices';
import {
  BANK_CESSION,
  BANK_CESSION_NAME,
  RadioGroupBankCession,
} from '@/features/v2/hh/components/RadioGroupBankCession';
import {
  DAMAGE_REPAIRED,
  DAMAGE_REPAIRED_NAME,
  RadioGroupDamageRepaired,
} from '@/features/v2/hh/components/RadioGroupDamageRepaired';
import RadioGroupDryingMethod, {
  DRYING_METHOD,
  DRYING_METHOD_NAME,
} from '@/features/v2/hh/components/RadioGroupDryingMethod';
import RadioGroupDryingSettlementForm, {
  SETTLEMENT_FORM_NAME,
} from '@/features/v2/hh/components/RadioGroupDryingSettlementForm';
import {
  FIX_METHOD,
  FIX_METHOD_NAME,
  RadioGroupFixMethod,
} from '@/features/v2/hh/components/RadioGroupFixMethod';
import {
  IS_COVERED_BY_MORTGAGE,
  IS_COVERED_BY_MORTGAGE_NAME,
  RadioGroupIsCoveredByMortgage,
} from '@/features/v2/hh/components/RadioGroupIsCoveredByMortgage';
import {
  INSURER_DECISION,
  INSURER_DECISION_NAME,
  RadioGroupIssuerDecision,
} from '@/features/v2/hh/components/RadioGroupIssuerDecision';
import {
  OWNERSHIP_TYPE,
  OWNERSHIP_TYPE_NAME,
  RadioGroupOwnershipType,
} from '@/features/v2/hh/components/RadioGroupOwnershipType';
import {
  PROPERTY_OWNERSHIP_STATUS,
  PROPERTY_OWNERSHIP_STATUS_NAME,
  RadioGroupPropertyOwnershipStatus,
} from '@/features/v2/hh/components/RadioGroupPropertyOwnershipStatus';
import {
  PROPERTY_TYPE,
  PROPERTY_TYPE_NAME,
  RadioGroupPropertyType,
} from '@/features/v2/hh/components/RadioGroupPropertyType';
import {
  RadioGroupSettlement,
  SETTLEMENT,
  SETTLEMENT_NAME,
} from '@/features/v2/hh/components/RadioGroupSettlement';
import {
  RadioGroupSettlementWasSufficient,
  SETTLEMENT_WAS_SUFFICIENT,
  SETTLEMENT_WAS_SUFFICIENT_NAME,
} from '@/features/v2/hh/components/RadioGroupSettlementWasSufficient';
import {
  RadioGroupUtilityPoles,
  UTILITY_POLES_NAME,
} from '@/features/v2/hh/components/RadioGroupUtilityPoles';
import {
  RadioGroupVatPayer,
  VAT_PAYER_NAME,
} from '@/features/v2/hh/components/RadioGroupVatPayer';
import {
  RadioGroupWillAppeal,
  WILL_APPEAL,
  WILL_APPEAL_NAME,
} from '@/features/v2/hh/components/RadioGroupWillAppeal';
import { BANK_NAME, SelectBank } from '@/features/v2/hh/components/SelectBank';
import {
  ISSUE_TYPE_NAME,
  SelectIssueType,
} from '@/features/v2/hh/components/SelectIssueType';
import {
  POLICY_TYPE,
  POLICY_TYPE_NAME,
  SelectPolicyType,
} from '@/features/v2/hh/components/SelectPolicyType';
import { TextareaDescribeDamage } from '@/features/v2/hh/components/TextareaDescribeDamage';
import {
  HHSchemaDeadEndsStepOne,
  HHSchemaDeadEndsStepOneNoIssueType,
  isInvoicesAnswered,
} from '@/features/v2/hh/stepsSchema';
import { useStepper } from '@/features/v2/lib/stepper';
import {
  getUserType,
  notQualifiedDataLayer,
} from '@/helpers/addStageToDataLyer';
import { EnumLike } from '@/types';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

// Order of answers on stage I. Changing an answer clears every answer below it,
// so no stale value survives in a question that got hidden and shown again.
const ANSWERS_ORDER = [
  ISSUE_TYPE_NAME,
  INSURER_DECISION_NAME,
  WILL_APPEAL_NAME,
  SETTLEMENT_NAME,
  HOW_MUCH_INSURER_PAID_NAME,
  DAMAGE_REPAIRED_NAME,
  HOW_MUCH_REPAIR_COST_NAME,
  SETTLEMENT_WAS_SUFFICIENT_NAME,
  FIX_METHOD_NAME,
  ANY_INVOICES_NAME,
  WHAT_WAS_REPAIRED_NAME,
  DRYING_METHOD_NAME,
  SETTLEMENT_FORM_NAME,
  POLICY_TYPE_NAME,
  IS_COVERED_BY_MORTGAGE_NAME,
  BANK_CESSION_NAME,
  BANK_NAME,
  PROPERTY_OWNERSHIP_STATUS_NAME,
  PROPERTY_TYPE_NAME,
  UTILITY_POLES_NAME,
  OWNERSHIP_TYPE_NAME,
  VAT_PAYER_NAME,
];

// Amount inputs are not triggers: they update the form on every keystroke
const NOT_RESETTING_ANSWERS = [
  HOW_MUCH_INSURER_PAID_NAME,
  HOW_MUCH_REPAIR_COST_NAME,
];

const SurveyOne = () => {
  const { i18n } = useLingui();
  const { innerObject } = useStepper();
  const { data } = useFormDataQuery();

  const innerStep = innerObject[0];

  const { getValues, watch, setValue } = useFormContext();

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      // type === 'change' only for user input, setValue below does not loop
      if (type !== 'change' || !name || NOT_RESETTING_ANSWERS.includes(name)) {
        return;
      }
      const index = ANSWERS_ORDER.indexOf(name);
      if (index === -1) return;

      ANSWERS_ORDER.slice(index + 1).forEach((answer) => {
        if (values[answer] !== null && values[answer] !== undefined) {
          setValue(answer, null);
        }
      });
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const issueType = data?.product.additional_info.issue_type?.value;
  const userType = getUserType(data);

  const willAppeal = watch(WILL_APPEAL_NAME);
  const insurerDecision = watch(INSURER_DECISION_NAME);

  useEffect(() => {
    if (
      insurerDecision === INSURER_DECISION.NEGATIVE &&
      willAppeal === WILL_APPEAL.NO
    ) {
      notQualifiedDataLayer(
        'negatywna_decyzja_brak_odwolania',
        'not_qaulify',
        userType
      );
    }
  }, [insurerDecision, willAppeal, userType]);

  const [selectedIssueType, setSelectedIssueType] = useState<string | null>(
    getValues(ISSUE_TYPE_NAME)
  );

  useEffect(() => {
    // check if watch() is not all reset as it is in the beginning
    if (Object.values(watch()).filter((item) => item !== undefined).length) {
      setSelectedIssueType(watch(ISSUE_TYPE_NAME));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues(ISSUE_TYPE_NAME)]);

  const isFlooding =
    issueType === FLOODING || Number(selectedIssueType) === FLOODING;

  const noAssignedIssueType = !issueType;

  const HHSchemaDeadEnds = useMemo(() => {
    if (noAssignedIssueType) {
      return HHSchemaDeadEndsStepOneNoIssueType;
    }

    return HHSchemaDeadEndsStepOne;
  }, [noAssignedIssueType]);

  return (
    <>
      {noAssignedIssueType && (
        <Card>
          <SelectIssueType disabled={innerStep >= 1} />
        </Card>
      )}

      {((noAssignedIssueType && innerStep >= 1) || !noAssignedIssueType) && (
        <>
          <Card isDeadEnd={HHSchemaDeadEnds.noIssueReportedToInsurer.condition}>
            <RadioGroupIssuerDecision />
            <ConditionalRender
              when={(val) =>
                val[INSURER_DECISION_NAME] === INSURER_DECISION.NEGATIVE
              }
            >
              <RadioGroupWillAppeal />
              <ConditionalRender
                when={(val) => val[WILL_APPEAL_NAME] === WILL_APPEAL.NO}
              >
                <CardDeadEndHint variant="primary">
                  <Hint.Title>
                    {i18n.t(
                      'Niestety, na ten moment nie możemy podjąć dalszych działań w Twojej sprawie'
                    )}
                  </Hint.Title>
                  <Hint.Description>
                    <p>
                      {i18n.t(
                        'Informujemy, że ze względu na decyzję ubezpieczyciela o odmowie wypłaty odszkodowania nie możemy kontynuować prowadzenia sprawy.'
                      )}
                    </p>
                    <p>
                      {i18n.t('Jednocześnie zachęcamy do rozważenia złożenia')}{' '}
                      {isFlooding ? (
                        <a
                          href="https://gen.helpfind.pl/odwolanie-od-decyzji-ubezpieczyciela-z-powodu-zalania-mieszkania/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline transition-colors hover:text-blue-700"
                        >
                          {i18n.t('odwołania od decyzji ubezpieczyciela')}
                        </a>
                      ) : (
                        i18n.t('odwołania od decyzji ubezpieczyciela')
                      )}
                      .{' '}
                      {i18n.t(
                        'Jeśli zostanie ono rozpatrzone pozytywnie i odszkodowanie zostanie przyznane, chętnie podejmiemy dalsze działania w Twojej sprawie.'
                      )}
                    </p>
                  </Hint.Description>
                </CardDeadEndHint>
              </ConditionalRender>
            </ConditionalRender>
            <ConditionalRender
              when={(val) =>
                val[INSURER_DECISION_NAME] === INSURER_DECISION.NO_DECISION
              }
            >
              <CardDeadEndHint variant="primary">
                <Hint.Title>{i18n.t('Pierwszy krok do współpracy')}</Hint.Title>
                <Hint.Description>
                  <p>
                    {i18n.t(
                      'Do momentu uzyskania pozytywnej decyzji ubezpieczyciela w Twojej sprawie, konieczne jest wstrzymanie dalszych działań z naszej strony.'
                    )}
                  </p>
                  <p>
                    {i18n.t(
                      'Zachęcamy do monitorowania postępów, a w przypadku otrzymania pozytywnej decyzji prosimy o niezwłoczny kontakt. Wówczas będziemy mogli podjąć odpowiednie kroki w celu dalszej realizacji sprawy.'
                    )}
                  </p>
                  <p>
                    {i18n.t(
                      'W razie dodatkowych pytań pozostajemy do dyspozycji.'
                    )}
                  </p>
                </Hint.Description>
              </CardDeadEndHint>
            </ConditionalRender>
          </Card>
          <ConditionalRender
            when={(val) =>
              val[INSURER_DECISION_NAME] === INSURER_DECISION.POSITIVE
            }
          >
            <Card isDeadEnd={HHSchemaDeadEnds.settlement.condition}>
              <RadioGroupSettlement />
              <CardDeadEndHint variant="primary">
                <Hint.Title>
                  {i18n.t(
                    'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
                  )}
                </Hint.Title>
                <Hint.Description>
                  <p>
                    {i18n.t(
                      'Ugoda, którą zawarto, oznacza rezygnację z dalszych roszczeń w sprawie szkody na nieruchomości i z tego powodu nie możemy podjąć kolejnych działań. Jeśli masz jakiekolwiek pytania lub wątpliwości, skontaktuj się z nami – chętnie wszystko wyjaśnimy.'
                    )}
                  </p>
                </Hint.Description>
              </CardDeadEndHint>
              <ConditionalRender
                when={(val) => val[SETTLEMENT_NAME] === SETTLEMENT.NO}
              >
                <InputHowMuchTheInsurerPaid />
              </ConditionalRender>
            </Card>
          </ConditionalRender>
        </>
      )}

      {innerStep >= (noAssignedIssueType ? 2 : 1) && (
        <Card isDeadEnd={HHSchemaDeadEnds.fixCostDifference.condition}>
          <RadioGroupDamageRepaired />

          <ConditionalRender
            when={(val) =>
              !!val[DAMAGE_REPAIRED_NAME] &&
              val[DAMAGE_REPAIRED_NAME] !== DAMAGE_REPAIRED.NO
            }
          >
            <InputHowMuchRepairCost />
          </ConditionalRender>

          <ConditionalRender
            when={HHSchemaDeadEnds.fixCostDifference.condition}
          >
            <CardDeadEndHint variant="primary">
              <Hint.Title>
                {i18n.t(
                  'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
                )}
              </Hint.Title>
              <Hint.Description>
                <p>
                  {i18n.t(
                    'Z przykrością informujemy, że nie mamy podstaw do zgłoszenia roszczenia do ubezpieczyciela – otrzymane odszkodowanie pokrywało koszty przywrócenia stanu sprzed szkody.'
                  )}
                </p>
              </Hint.Description>
            </CardDeadEndHint>
          </ConditionalRender>
        </Card>
      )}

      {innerStep >= (noAssignedIssueType ? 3 : 2) && (
        <>
          <ConditionalRender
            when={(val) => {
              return (
                !!val[HOW_MUCH_REPAIR_COST_NAME] &&
                val[DAMAGE_REPAIRED_NAME] !== DAMAGE_REPAIRED.NO &&
                val[INSURER_DECISION_NAME] !== INSURER_DECISION.NEGATIVE
              );
            }}
          >
            <Card isDeadEnd={HHSchemaDeadEnds.fixCostEnough.condition}>
              <RadioGroupSettlementWasSufficient />

              <ConditionalRender
                when={HHSchemaDeadEnds.fixCostEnough.condition}
              >
                <CardDeadEndHint variant="primary">
                  <Hint.Title>
                    {i18n.t(
                      'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
                    )}
                  </Hint.Title>
                  <Hint.Description>
                    <p>
                      {i18n.t(
                        'Informujemy, że w obecnej sytuacji, ze względu na decyzję ubezpieczyciela o wypłacie odszkodowania, które wystarczyło na przywrócenie nieruchomości do stanu sprzed szkody, nie możemy dalej prowadzić Twojej sprawy.'
                      )}
                    </p>
                  </Hint.Description>
                </CardDeadEndHint>
              </ConditionalRender>
            </Card>
          </ConditionalRender>

          <ConditionalRender
            when={(val) =>
              val[SETTLEMENT_WAS_SUFFICIENT_NAME] ===
                SETTLEMENT_WAS_SUFFICIENT.NO ||
              (val[INSURER_DECISION_NAME] === INSURER_DECISION.NEGATIVE &&
                !!val[HOW_MUCH_REPAIR_COST_NAME] &&
                val[DAMAGE_REPAIRED_NAME] !== DAMAGE_REPAIRED.NO)
            }
          >
            <Card>
              <RadioGroupFixMethod />
              <ConditionalRender
                when={(val) =>
                  !!val[FIX_METHOD_NAME] &&
                  val[FIX_METHOD_NAME] !== FIX_METHOD.ECONOMIC_METHOD
                }
              >
                <RadioGroupAnyInvoices />
              </ConditionalRender>
            </Card>
          </ConditionalRender>
          <ConditionalRender
            when={(val) =>
              isFlooding &&
              val[DAMAGE_REPAIRED_NAME] !== DAMAGE_REPAIRED.NO &&
              isInvoicesAnswered(val)
            }
          >
            <Card>
              <CheckboxGroupWhatWasRepaired />
              <ConditionalRender
                when={(val) =>
                  isFlooding &&
                  val[WHAT_WAS_REPAIRED_NAME]?.some(
                    (item: EnumLike) =>
                      item.value === WHAT_WAS_REPAIRED_OPTIONS.DRYING
                  )
                }
              >
                <RadioGroupDryingMethod />
                <ConditionalRender
                  when={(val) =>
                    val[DRYING_METHOD_NAME] === DRYING_METHOD.DRYING_COMPANY
                  }
                >
                  <RadioGroupDryingSettlementForm />
                </ConditionalRender>
              </ConditionalRender>
            </Card>
          </ConditionalRender>

          <ConditionalRender
            when={(val) =>
              (isFlooding &&
                ((val[DAMAGE_REPAIRED_NAME] === DAMAGE_REPAIRED.NO &&
                  isInvoicesAnswered(val)) ||
                  (val[DAMAGE_REPAIRED_NAME] !== DAMAGE_REPAIRED.NO &&
                    val[WHAT_WAS_REPAIRED_NAME]?.length &&
                    ((val[WHAT_WAS_REPAIRED_NAME]?.some(
                      (item: EnumLike) =>
                        item.value === WHAT_WAS_REPAIRED_OPTIONS.DRYING
                    ) &&
                      ((val[DRYING_METHOD_NAME] ===
                        DRYING_METHOD.DRYING_COMPANY &&
                        !!val[SETTLEMENT_FORM_NAME]) ||
                        val[DRYING_METHOD_NAME] ===
                          DRYING_METHOD.EQUIPMENT_RENTAL)) ||
                      !val[WHAT_WAS_REPAIRED_NAME]?.some(
                        (item: EnumLike) =>
                          item.value === WHAT_WAS_REPAIRED_OPTIONS.DRYING
                      ))))) ||
              ((isFlooding || !isFlooding) &&
                val[DAMAGE_REPAIRED_NAME] === DAMAGE_REPAIRED.NO) ||
              (!isFlooding &&
                val[DAMAGE_REPAIRED_NAME] !== DAMAGE_REPAIRED.NO &&
                isInvoicesAnswered(val))
            }
          >
            <Card>
              <SelectPolicyType />

              <ConditionalRender
                when={(val) =>
                  [
                    POLICY_TYPE.OWN_POLICY,
                    POLICY_TYPE.AGRICULTURAL_POLICY,
                  ].includes(val[POLICY_TYPE_NAME])
                }
              >
                <RadioGroupIsCoveredByMortgage />
              </ConditionalRender>

              <ConditionalRender
                when={(val) =>
                  val[IS_COVERED_BY_MORTGAGE_NAME] ===
                  IS_COVERED_BY_MORTGAGE.YES
                }
              >
                <RadioGroupBankCession />
              </ConditionalRender>

              <ConditionalRender
                when={(val) => val[BANK_CESSION_NAME] === BANK_CESSION.YES}
              >
                <SelectBank />
              </ConditionalRender>
            </Card>
          </ConditionalRender>

          <ConditionalRender
            when={(val) =>
              (!!val[POLICY_TYPE_NAME] &&
                ![
                  POLICY_TYPE.OWN_POLICY,
                  POLICY_TYPE.AGRICULTURAL_POLICY,
                ].includes(val[POLICY_TYPE_NAME])) ||
              val[IS_COVERED_BY_MORTGAGE_NAME] === IS_COVERED_BY_MORTGAGE.NO ||
              val[BANK_CESSION_NAME] === BANK_CESSION.NO ||
              !!val[BANK_NAME]
            }
          >
            <Card>
              <RadioGroupPropertyOwnershipStatus />
              <ConditionalRender
                when={(val) =>
                  val[PROPERTY_OWNERSHIP_STATUS_NAME] &&
                  [
                    PROPERTY_OWNERSHIP_STATUS.OWNER,
                    PROPERTY_OWNERSHIP_STATUS.CO_OWNER,
                  ].includes(val[PROPERTY_OWNERSHIP_STATUS_NAME])
                }
              >
                <RadioGroupPropertyType />
              </ConditionalRender>
              <ConditionalRender
                when={(val) => val[PROPERTY_TYPE_NAME] === PROPERTY_TYPE.HOUSE}
              >
                <RadioGroupUtilityPoles />
              </ConditionalRender>
              <ConditionalRender
                when={(val) =>
                  !!val[PROPERTY_OWNERSHIP_STATUS_NAME] &&
                  (val[PROPERTY_OWNERSHIP_STATUS_NAME] ===
                    PROPERTY_OWNERSHIP_STATUS.TENANT ||
                    val[PROPERTY_TYPE_NAME] === PROPERTY_TYPE.APARTMENT ||
                    (val[PROPERTY_TYPE_NAME] === PROPERTY_TYPE.HOUSE &&
                      val[UTILITY_POLES_NAME]))
                }
              >
                <RadioGroupOwnershipType />
              </ConditionalRender>
              <ConditionalRender
                when={(val) =>
                  val[OWNERSHIP_TYPE_NAME] === OWNERSHIP_TYPE.COMPANY
                }
              >
                <RadioGroupVatPayer />
              </ConditionalRender>
            </Card>
          </ConditionalRender>
        </>
      )}

      {innerStep >= (noAssignedIssueType ? 4 : 3) && (
        <ConditionalRender
          when={(val) =>
            val[OWNERSHIP_TYPE_NAME] === OWNERSHIP_TYPE.PRIVATE ||
            (val[OWNERSHIP_TYPE_NAME] === OWNERSHIP_TYPE.COMPANY &&
              !!val[VAT_PAYER_NAME])
          }
        >
          <Card>
            <TextareaDescribeDamage />
          </Card>
        </ConditionalRender>
      )}
    </>
  );
};

export default SurveyOne;
